/// <reference path="../groupthink-js.d.ts" />

import React, { useCallback } from 'react';
import useSWR, { mutate as globalMutate, useSWRConfig } from 'swr';
import useSWRInfinite from 'swr/infinite';
import { fetcher, apiRequest } from '../lib';
import { useUser } from './user';
import { useUserAgendas } from './agenda';
import { usePusher } from './realtime';
import { produce } from 'immer';

export const useMeeting = (
  agendaId?: string,
  id?: string,
  options?: {
    useRealtimeResource?: Groupthink.RealtimeResourceHandler<Groupthink.MeetingResource>;
  }
) => {
  const url = agendaId && id ? `/v1/agendas/${agendaId}/meetings/${id}` : null;
  const { useRealtimeResource } = options || {};
  const {
    data: meeting,
    error,
    isLoading,
    mutate,
  } = useSWR<Groupthink.SuccessfulResponseContent<'meeting.show'>>(url, fetcher, {
    keepPreviousData: true,
  });

  useRealtimeResource?.(
    '.MeetingUpdated',
    id ? `App.Models.Agenda.Meeting.${id}` : false,
    mutate,
    url
  );

  const sendRecap = <RouteName = 'meeting.sendRecap'>(
    email: string, // email to send recap to
    { setErrors, setIsUpdating, onSuccess }: Groupthink.UpdateOperationOptions<RouteName> = {}
  ) =>
    apiRequest<RouteName>(`/v1/agendas/${agendaId}/meetings/${id}/send_recap`, mutate, 'POST', {
      setErrors,
      setLoading: setIsUpdating,
      payload: { email },
      onSuccess,
    });

  const updateMeeting = useCallback(
    <RouteName = 'meeting.update'>({
      setErrors,
      setIsUpdating,
      onSuccess,
      payload,
    }: Groupthink.UpdateOperationOptions<RouteName> = {}) =>
      apiRequest<RouteName>(`/v1/agendas/${agendaId}/meetings/${id}`, mutate, 'PUT', {
        setErrors,
        setLoading: setIsUpdating,
        payload,
        onSuccess,
      }),
    [agendaId, mutate]
  );

  return {
    meeting: meeting?.data,
    isLoading,
    isError: error,
    mutate,
    sendRecap,
    updateMeeting,
  };
};

export const useTranscription = (
  agendaId?: string,
  meetingId?: string,
  options?: {
    refreshInterval?: number;
    revalidateOnFocus?: boolean;
    usePusher?: Groupthink.RealtimePusherHandler;
  }
) => {
  const { refreshInterval = 0, usePusher } = options || {};
  const url =
    Boolean(agendaId) && Boolean(meetingId)
      ? `/v1/agendas/${agendaId}/meetings/${meetingId}/transcription`
      : false;

  const handleErrorRetry = useCallback(
    (error, key, config, revalidate, { retryCount }) => {
      // API will return 404 if transcription is not available yet. This can happen if the bot has not yet joined the meeting.
      // In this case, we want to retry after a short delay.
      if (error.status === 404) {
        console.info(
          `Transcription not available yet for agenda ${agendaId} meeting ${meetingId}. Retrying in 5 seconds...`
        );
        setTimeout(() => revalidate({ retryCount }), 5_000);
      }

      // Use the default backoff algorithm otherwise.
      // @ts-ignore
      onErrorRetry(error, key, config, revalidate, { retryCount });
    },
    [agendaId, meetingId]
  );

  const { onErrorRetry } = useSWRConfig();
  const {
    data: transcription,
    error,
    isLoading,
    mutate,
  } = useSWR<Groupthink.SuccessfulResponseContent<'meeting.transcription'>>(() => url, fetcher, {
    refreshInterval,
    keepPreviousData: true,
    // If we're using Pusher, we don't want to revalidate on focus, reconnect, or if stale.
    revalidateOnFocus: !usePusher,
    revalidateOnReconnect: !usePusher,
    revalidateIfStale: !usePusher,
    revalidateOnMount: true,
    onErrorRetry: handleErrorRetry,
  });

  const handleTranscriptionSentenceCreated = useCallback(
    (newSentence) => {
      if (!newSentence || !newSentence.transcription_id) {
        console.warn('Received transcription event with missing data:', newSentence);
        return;
      }

      return mutate(
        // @ts-ignore
        (previousTranscription) => {
          // if previousTranscription is undefined, return it
          if (!previousTranscription) {
            return previousTranscription;
          }

          if (Object.hasOwnProperty.call(previousTranscription, 'data')) {
            // @ts-ignore
            const exists = previousTranscription.data.content.some(
              (item) => item.id === newSentence.id
            );

            if (!exists) {
              return {
                data: produce(previousTranscription.data, (draft) => {
                  // @ts-ignore
                  draft.content.push({ ...newSentence, streamable: true });
                }),
              };
            }
          }

          return previousTranscription;
        },
        {
          // don't revalidate SWR cache
          revalidate: false,
          // this is a special SWR function that allows us to update the cache manually
          populateCache: (cache) => {
            return cache;
          },
        }
      );
    },
    [mutate]
  );

  // We'd normally use useRealtimeCollection, but our array of sentences is in transcription.data.content instead of transcription.data.
  // We could potentially extend useRealtimeCollection to handle this case, but for now we'll just use usePusher directly.
  usePusher?.({
    type: 'Meeting\\Transcription\\SentenceCreated',
    channel: agendaId ? `agendas.${agendaId}.notes` : null,
    url: null,
    // we're going to use the callback when pusher gets new data to merge in the payload from pusher.
    // we have to assume that pusher doesn't have the entire object, so we're going to merge it ourselves.
    callback: handleTranscriptionSentenceCreated,
  });

  return {
    transcription: transcription?.data,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useTranscriptionNotes = (
  agendaId?: string,
  id?: string,
  /**
   * If null, fetch notes for all agenda items in the meeting.
   */
  agenda_item_id?: string,
  options?: {
    refreshInterval?: number;
    useRealtimeCollection?: Groupthink.RealtimeCollectionHandler<Groupthink.MessageResource>;
  }
) => {
  const { refreshInterval = 0, useRealtimeCollection } = options || {};

  const url =
    Boolean(agendaId) && Boolean(id) && Boolean(agenda_item_id)
      ? `/v1/agendas/${agendaId}/meetings/${id}/transcription_notes/${agenda_item_id}`
      : Boolean(agendaId) && Boolean(id)
        ? `/v1/agendas/${agendaId}/meetings/${id}/transcription_notes`
        : false;

  const {
    data: transcription_notes,
    error,
    isLoading,
    mutate,
  } = useSWR<Groupthink.SuccessfulResponseContent<'meeting.transcriptionNotes'>>(
    () => url,
    fetcher,
    {
      refreshInterval,
      keepPreviousData: true,
      // If we're using a realtime collection, we don't want to revalidate on focus, reconnect, or if stale.
      revalidateOnFocus: !useRealtimeCollection,
      revalidateOnReconnect: !useRealtimeCollection,
      revalidateIfStale: !useRealtimeCollection,
    }
  );
  useRealtimeCollection?.(
    'Meeting.NoteCreated',
    agendaId ? `agendas.${agendaId}.notes` : null,
    mutate,
    url,
    'create',
    false
  );

  return {
    // @ts-ignore
    transcription_notes: transcription_notes?.data,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useMeetings = (agendaId?: string) => {
  const getKey = (pageIndex, previousPageData) => {
    if (!agendaId) {
      return null;
    }

    // reached the end
    if (previousPageData && !previousPageData.data) return null;

    // first page, we don't have `previousPageData`
    if (pageIndex === 0) return `/v1/agendas/${agendaId}/meetings`;

    return `/v1/agendas/${agendaId}/meetings?cursor=${previousPageData.meta.next_cursor}`;
  };

  const { data, error, size, setSize, isLoading, mutate } = useSWRInfinite(
    (pageIndex, previousPageData) => getKey(pageIndex, previousPageData),
    fetcher,
    {
      keepPreviousData: true,
      revalidateFirstPage: false,
    }
  );

  const hasMorePages = data?.[data?.length - 1]?.meta?.next_cursor !== null;

  const meetings = React.useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    return data.reduce((allMeetings, page, index) => {
      if (!page) {
        console.warn(`Page ${index} is undefined`);
        return allMeetings;
      }

      if (Array.isArray(page)) {
        return [...allMeetings, ...page];
      }

      if (typeof page === 'object' && Array.isArray(page.data)) {
        return [...allMeetings, ...page.data];
      }

      console.warn(`Unexpected page structure at index ${index}:`, page);
      return allMeetings;
    }, []);
  }, [data]);

  const active_meeting = meetings?.find((m) => !m.ended_at && m.started_at);

  const createMeeting = ({ setErrors, setIsCreating, onSuccess, payload }) =>
    apiRequest<'meeting.store'>(`/v1/agendas/${agendaId}/meetings`, mutate, 'POST', {
      setErrors,
      setLoading: setIsCreating,
      payload,
      onSuccess,
    });

  const updateMeeting = useCallback(
    <RouteName = 'meeting.update'>(
      meetingId: string,
      {
        setErrors,
        setIsUpdating,
        onSuccess,
        payload,
      }: Groupthink.UpdateOperationOptions<RouteName> = {}
    ) =>
      apiRequest<RouteName>(`/v1/agendas/${agendaId}/meetings/${meetingId}`, mutate, 'PUT', {
        setErrors,
        setLoading: setIsUpdating,
        payload,
        onSuccess,
      }),
    [agendaId, mutate]
  );

  const deleteMeeting = async <RouteName = 'meeting.destroy'>(
    meetingId: string,
    { setErrors, setIsDeleting, onSuccess }: Groupthink.DeleteOperationOptions<RouteName> = {}
  ) =>
    apiRequest<RouteName>(`/v1/agendas/${agendaId}/meetings/${meetingId}`, mutate, 'DELETE', {
      setErrors,
      setLoading: setIsDeleting,
      onSuccess,
    });

  const endMeeting = useCallback(
    (options?: { meetingId?: string } & Groupthink.UpdateOperationOptions<'meeting.update'>) => {
      const meetingId = options?.meetingId ?? active_meeting?.id;
      if (meetingId) {
        // @ts-ignore
        return updateMeeting(meetingId, { payload: { end: true }, ...options });
      }

      return;
    },
    [active_meeting, updateMeeting]
  );

  return {
    meetings,
    active_meeting,
    isLoading,
    hasMorePages,
    size,
    setSize,
    isError: error,
    mutate,
    createMeeting,
    updateMeeting,
    deleteMeeting,
    endMeeting,
  };
};

// create meeting function available without agenda#show api call
// currently not mutating
export const createMeeting = ({ agendaId, setErrors, setIsCreating, onSuccess, payload }) =>
  apiRequest<'meeting.store'>(`/v1/agendas/${agendaId}/meetings`, null, 'POST', {
    setErrors,
    setLoading: setIsCreating,
    payload,
    onSuccess,
  });

// Payload should be {recordings: [{ recordingType: mic/systemAudio/etc, fileType: mp3/mp4/etc }]}
export const createMeetingRecordingUploadURLs = ({
  agendaId,
  meeting_id,
  setErrors,
  setIsCreating,
  onSuccess,
  payload,
}) =>
  apiRequest<'meeting.store'>(
    `/v1/agendas/${agendaId}/meetings/${meeting_id}/recording_upload_urls`,
    null,
    'POST',
    {
      setErrors,
      setLoading: setIsCreating,
      payload,
      onSuccess,
    }
  );

// the URL the user should be at during this active meeting
// or the summary URL if the meeting is over
export const selectMeetingUrl = (meeting) => {
  if (!meeting) return null;

  if (meeting.started_at && !meeting.ended_at) {
    return `/agendas/${meeting.agenda_id}`;
  }

  return `/agendas/${meeting.agenda_id}/meetings/${meeting.id}`;
};

/**
 * Returns the user's active meetings, if any. This hook listens to Pusher events to keep the data up-to-date.
 */
export const useActiveMeetings = () => {
  const { agendas, isLoading, mutate } = useUserAgendas(null, 'active');
  const { user } = useUser('me');

  const activeMeetings = (agendas ?? [])
    ?.filter(
      (agenda: Groupthink.AgendaResource) =>
        (agenda?.active_meeting?.starts_at || agenda?.active_meeting?.ends_at) &&
        agenda?.active_meeting?.ended_at === null
    )
    .map((agenda: Groupthink.AgendaResource) => agenda.active_meeting);

  const channel = user ? `users.${user?.id}.meetings` : null;

  // Merge incoming MeetingResource with the existing agendas data
  const callback = (meetingObject) =>
    mutate(
      // @ts-ignore
      (agendasObject) => {
        // if agendasObject is undefined, return it
        if (!agendasObject) {
          return agendasObject;
        }

        // clone the entire API payload we retrieved from the server earlier so SWR knows that it's changed
        const clonedAgendasObject = { ...agendasObject };

        // find the agenda with a meeting that was updated in our pusher payload
        const clonedAgendasObjectData =
          clonedAgendasObject !== undefined && 'data' in clonedAgendasObject
            ? clonedAgendasObject.data
            : undefined;

        const agendaWithTouchedMeeting = clonedAgendasObjectData?.find(
          (item) => item && item.id === meetingObject?.agenda_id
        );

        // filter out the agenda with a meeting that was updated from the list of agendas
        const allOtherItems = clonedAgendasObjectData?.filter(
          (item) => item && item.id !== meetingObject?.agenda_id
        );

        // merge the updated agenda with the payload from pusher
        const updatedItems = [
          ...(allOtherItems ?? []),
          agendaWithTouchedMeeting && {
            ...agendaWithTouchedMeeting,
            active_meeting: agendaWithTouchedMeeting?.active_meeting
              ? { ...agendaWithTouchedMeeting.active_meeting, ...meetingObject }
              : meetingObject,
          },
        ];

        // return the new agendas object with the updated agenda and meeting
        return {
          ...clonedAgendasObject,
          data: updatedItems,
        };
      },
      {
        // don't revalidate SWR cache
        revalidate: false,
        // this is a special SWR function that allows us to update the cache manually
        populateCache: (cache) => {
          return cache;
        },
      }
    );

  usePusher({
    type: 'Meeting.MeetingStarted',
    channel,
    callback,
  });

  usePusher({
    type: 'Meeting.MeetingEnded',
    channel,
    callback,
  });

  return { activeMeetings, isLoading };
};

export const updateMeeting = <RouteName = 'meeting.update'>(
  agendaId: string,
  meetingId: string,
  {
    setErrors,
    setIsUpdating,
    onSuccess,
    payload,
  }: Groupthink.UpdateOperationOptions<RouteName> = {}
) => {
  const url = `/v1/agendas/${agendaId}/meetings/${meetingId}`;
  const mutate = () => globalMutate(url);
  return apiRequest<RouteName>(url, mutate, 'PUT', {
    setErrors,
    setLoading: setIsUpdating,
    payload,
    onSuccess,
  });
};

export default useActiveMeetings;
